<script setup>
import { enFetch } from 'Js/utils/fetchUtils';
import { sleep } from 'Js/utils/sleep';
import { nextTick, onBeforeMount, onMounted, computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import VarType from 'Js/enums/varType';
import { ComboboxItem } from 'reka-ui';
import { X } from 'lucide-vue-next';
import { Button } from 'Components/primitives/button';
import { Label } from 'Components/primitives/label';
import { ComboboxRoot, ComboboxAnchor, ComboboxInput, ComboboxContent, ComboboxEmpty } from 'Components/primitives/combobox';
import { useValidation } from 'Components/inputs/useValidation.ts';

const { t } = useI18n();
const modelValue = defineModel();
/**
 * A wrapper around vuetify's auto-complete component, which uses remote data with a DevExtreme like controller for loading.
 */
const props = defineProps({
  /**
   * Value of the text field. If value is an object, pass it for correct display
   */
  preselectedObject: Object,
  /**
   * The items available for static autocomplete.
   */
  items: {
    type: Array,
    default: () => [],
  },
  /**
   * Property name used to identify unique items.
   * Defaults to 'Id'.
   */
  itemValue: {
    type: String,
    default: 'Id'
  },
  /**
   * String displayed as the label of the text field.
   */
  label: String,
  /**
   When searching, will always highlight the first option and select it on blur. exact will only highlight and select exact matches.
   */
  autoSelectFirst: {
    type: Boolean,
    default: false
  },
  /**
   * Property name or function that takes an item returned from the endpoint and returns the string that should be displayed.
   */
  displayExpression: {
    type: [String, Function],
    default: 'Name'
  },
  /**
   * Either the name of a model property that is searched in, or a function that takes a search string and returns a devextreme filter in array form
   */
  searchExpression: [String, Function],
  /**
   * Either the value to be considered in the filter or a function altering the input value
   */
  searchValue: [String, Function],
  /**
   * A devextreme select in array form.
   */
  selectExpression: Array,
  /**
   * Uri to send the request to.
   * If set the remote autocomplete is used.
   * If not set the static autocomplete is used.
   */
  apiUri: String,
  /**
   * Validation rules for the text field.
   */
  rules: Array,
  /**
   * Field name used by vee-validate.
   */
  name: String,
  /**
   * Static ID for the field, useful for testing or specific styling.
   * If not provided, no static ID is applied.
   */
  staticId: [String, Number],
  /**
   * String displayed as the hint of the text field.
   */
  hint: String,
  /**
   * Specifies whether the field shows the values in multiple lines (true) or not (false).
   */
  singleLine: Boolean,
  /**
   * Specifies whether the hint is persistently shown (true) or not (false).
   */
  persistentHint: Boolean,
  /**
   * Used to get distinct values.
   * e.g. model="CustomerType" results in the items being loaded to become distinct values of CustomerType.
   */
  group: String,
  /**
   * Used with group. Remove value = null
   *
   */
  hideNullValues: {
    type: Boolean,
    default: false
  },
  /**
   * Specifies whether the clear button is shown (true) or not (false).
   */
  clearable: Boolean,
  /**
   * Specifies whether the text field is focused initially (true) or not (false).
   */
  autofocus: Boolean,
  /**
   * Specifies whether the text field is disabled (true) or not (false).
   */
  disabled: Boolean,
  /**
   * Specifies whether focusing on the input triggers a reload.
   */
  reloadOnFocusIn: Boolean,
  /**
   * Specifies id for input.
   */
  id: String,
  /**
   * Specifies whether text field is marked as required (true) or not (false).
   * Required text fields have a "*" added to the label.
   */
  required: Boolean,
  /**
   * Specifies whether the input is readonly.
   * See: https://vuetifyjs.com/en/api/v-autocomplete/#props-readonly
   */
  readonly: Boolean,
  /**
   * Specifies whether the outlined style is applied (true) or not (false).
   * See: https://vuetifyjs.com/en/api/v-autocomplete/#props-outlined
   */
  outlined: Boolean,
  /**
   * Specifies whether dense style is applied (true) or not (false).
   * Defaults to true.
   */
  dense: {
    type: Boolean,
    default: true,
  },
  /**
   * Specifies whether autocomplete should use "contains" instead of equals
   * Defaults to false.
   */
  useContains: {
    type: Boolean,
    default: false,
  },
  returnObject: {
    type: Boolean,
    default: false
  },
});
const selectedItemObject = ref(null);
const loading = ref(false);
const currentItems = ref([]);
const inputValue = ref(null);
let lastReloadFilter = null;
let lastReloadPage = null;
let lastReloadTimestamp = null;
let allItemsLoaded = false;
const containsPrefix = ref(t('common.filters.contains'));
let page = 0;
let isContains = false;
const isClearedByUser = ref(false);
const emit = defineEmits([
  'selected-item-object-changed',
  'input',
  'input-value',
  'set-has-prefix',
  'update:modelValue',
  'change',
  'reset-value',
  'enter-pressed',
  'mounted',
]);

const isMenuOpen = ref(false);

const selectedItem = computed(() => modelValue.value);
const isDirty = ref(false);

const {
  errorMessage,
  onBlur: onValidationBlur,
} = useValidation(isDirty, props, modelValue);

const hasErrors = computed(() => {
  return !!errorMessage.value;
});

function getItemValue(item) {
  if (item == null || typeof item !== VarType.Object) {
    return item;
  }

  return item?.[props.itemValue];
}

function getPreselectedObject(value = selectedItem.value) {
  if (value == null) {
    return null;
  }

  // Object values already contain the fields needed for display.
  if (props.returnObject || typeof value === VarType.Object) {
    return value;
  }

  const loadedItem = currentItems.value.find(item => getItemValue(item) === value);
  if (loadedItem) {
    return loadedItem;
  }

  // Prefer the explicit object supplied by the parent when the selected value
  // is only an id/value and the item is not loaded yet.
  if (props.preselectedObject && getItemValue(props.preselectedObject) === value) {
    return props.preselectedObject;
  }

  // Function display expressions may need fields that cannot be derived from a
  // primitive selected value, so keep the raw value.
  if (typeof props.displayExpression === VarType.Function) {
    return value;
  }

  // Build a minimal placeholder object so the selected primitive can still be
  // displayed until the real item is loaded.
  const preselectedObject = {
    [props.itemValue]: value,
  };

  if (typeof props.displayExpression === VarType.String) {
    preselectedObject[props.displayExpression] = value;
  }

  return preselectedObject;
}

function setSelectedItemObjectFromValue(value = selectedItem.value) {
  selectedItemObject.value = getPreselectedObject(value);
  const selectedItemValue = getItemValue(value);

  if (selectedItemObject.value && !currentItems.value.some(item => getItemValue(item) === selectedItemValue)) {
    currentItems.value = [selectedItemObject.value, ...currentItems.value];
  }
}

function handleItemSelect(item) {
  if (item === null && !isClearedByUser.value) return;

  inputValue.value = null;

  if (isContains && typeof item === VarType.String) {
    selectedItemObject.value = { [props.itemValue]: item };
    /**
     * Triggers when the selectedItemObject has changed.
     */
    emit('selected-item-object-changed', selectedItemObject.value);
    /**
     * Triggers when the value has changed.
     */
    emit('input', item);
    emit('input-value', item);
    emit('set-has-prefix', true);
    emit('update:modelValue', item);
  } else {
    selectedItemObject.value = item;
    // For plain string items, emit the string directly rather than item[itemValue]
    // which would be undefined.
    const emitValue = props.returnObject
      ? item
      : (item != null
          ? (typeof item === VarType.String ? item : item[props.itemValue])
          : null);
    
    /**
     * Triggers when the selectedItemObject has changed.
     */
    emit('selected-item-object-changed', item);
    /**
     * Triggers when the value has changed.
     */
    emit('input', emitValue);
    emit('input-value', emitValue);
    emit('set-has-prefix', false);
    emit('update:modelValue', emitValue);
    
  }

  isContains = false;
}

function searchContains() {
  isContains = true;
  handleItemSelect(inputValue.value);
  isMenuOpen.value = false;
}

function getDisplayExpression(value) {
  if (value && typeof props.displayExpression === VarType.Function) {
    return props.displayExpression(value);
  } else if (value && props.displayExpression) {
    // Plain strings don't have named properties, fall back to the string itself.
    if (typeof value === VarType.String) return value;
    const result = value[props.displayExpression];
    return result !== undefined ? result : String(value);
  }
  return value ?? '';
}
function onBlur() {
  isDirty.value = true;
  onValidationBlur();
}

function inputChanged(val) {
  isDirty.value = true;
  inputValue.value = val;
  // reset autoloader
  page = 0;
  if (val === null) {
    allItemsLoaded = false;
  }
  // reka-ui clears the search term after selection without triggering a native input event
  // so this is always triggered by user input.
  if (val && props.apiUri) {
    loadItems(val);
  }
  emit('change', val);
}

function getFilter(value) {
  let filter = null;
  let searchValue = value;

  if (props.searchValue) {
    if (typeof props.searchValue === VarType.String) searchValue = props.searchValue;
    if (typeof props.searchValue === VarType.Function) searchValue = props.searchValue(searchValue);
  }

  if (typeof props.searchExpression === VarType.String && searchValue) {
    filter = [props.searchExpression, 'contains', searchValue];
  } else if (typeof props.searchExpression === VarType.Function) {
    filter = props.searchExpression.call(props.searchExpression, searchValue);
  }

  return filter;
}

async function loadItems(searchValue, force = false) {
  if (!props.apiUri) return;

  const filterString = JSON.stringify(getFilter(searchValue));
  if (!force && filterString === lastReloadFilter && lastReloadPage === page) {
    // prevent unnecessary API calls
    return;
  }

  loading.value = true;
  if (page === 0) {
    currentItems.value = [];
  }
  lastReloadFilter = filterString;
  lastReloadPage = page;

  const currentTimeStamp = new Date();
  // This logic reduces number of requests to server by waiting for at least 0.5s before send
  // For example
  //    User types 'e', and then immediately 'n' ('en') and 'e' again. 
  //    Instead of sending 3 requests (one for 'e', one for 'en' and one for 'ene') we send only 'ene'. 
  // If filter is empty, we don't have to wait
  if (filterString !== '[]') {
    // Mark request timestamp
    lastReloadTimestamp = currentTimeStamp;
    // Wait for 0.5s. If this value is changed, then search value is changed and we don't have to do anything.
    // If search value didn't change for 0.5s, send request.
    await sleep(500);

    // Simply forget about the request
    if (currentTimeStamp - lastReloadTimestamp !== 0) {
      return;
    }
    lastReloadTimestamp = null;
  }
  await nextTick();

  const res = await enFetch({
    url: props.apiUri + '?' + new URLSearchParams({
      skip: page * 10,
      take: 10,
      requireTotalCount: false,
      requireGroupCount: false,
      distinct: true, // for our new EnLoader, not support by DevExtremeDataLoader
      filter: filterString,
      select: props.selectExpression ? JSON.stringify(props.selectExpression) : null,
      group: !props.group ? null : JSON.stringify([{selector: props.group, isExpanded: false}])
    })
  });
  if (filterString !== lastReloadFilter) {
    // this can only be the case if a new loadItems process has since been started
    // in that case, don't set items, because it is not guaranteed that they happen in order
    // e.g., it could be that
    // Reload-A .....  Reload-B  ... Then-B ... Then-A
    // if the processing for A takes longer then the processing for B
    // if we would set items, we could reset them to an older, invalid state
    return;
  }
  if (res.ok) {
    let resItems = (await res.json()).data;
    // devextreme-like controllers return a completely different structure when the parameter
    // group is given, so we need to map it into the structure expected by the widget
    if (props.group) {
      resItems = resItems.map(i => {
        let mappedItem = {};
        mappedItem[props.group] = i.key;
        return mappedItem;
      });
      if (props.hideNullValues) {
        resItems = resItems.filter(i => i[props.group] !== null);
      }
    } else {
      if (props.hideNullValues) {

        if (typeof props.displayExpression !== VarType.Function) {
          resItems = resItems.filter(i => i[props.displayExpression] !== null && i[props.displayExpression] !== '');
        } else {
          resItems = resItems.filter(i => props.displayExpression(i) !== null && props.displayExpression(i) !== '');
        }
      }
    }
    // If page is not 0, then we load additional 10 items
    if (page !== 0) {
      currentItems.value = [...currentItems.value, ...resItems];

      // 0 items means only that we reached last available page
      if (resItems.length === 0) {
        allItemsLoaded = true;
      }
    } else {
      currentItems.value = resItems;
    }
  }
  
  loading.value = false;
}

function valueReset() {
  isClearedByUser.value = true;
  isContains = false;
  inputValue.value = undefined;
  selectedItemObject.value = undefined;
  emit('update:modelValue', undefined);
  emit('reset-value');
  isClearedByUser.value = false;

  if (props.apiUri) {
    page = 0;
    allItemsLoaded = false;
    loadItems(null);
  }
}

function detectScrollEnd(val) {
  if (!val.target) {
    return;
  }

  // Track for end of scrollbar. scrollTop sometimes too precise (double) and we would like to use exact int
  if (val.target.scrollHeight - Math.round(val.target.scrollTop) - val.target.clientHeight <= 2 && !allItemsLoaded && !loading.value) {
    // Load additional 10 items
    page++;
    const valueForFilter =  selectedItem.value === '' ? null : selectedItem.value;
    loadItems(valueForFilter ?? inputValue.value);
  }
}

async function focusIn() {
  if (props.readonly || props.disabled) return;

  if (props.reloadOnFocusIn) {
    await loadItems(selectedItem.value ?? inputValue.value, true);
  }
}

async function updateMenu(val) {
  isMenuOpen.value = val;
}

function forceSelection() {
  if (!inputValue.value) return;

  const match = currentItems.value.find(item => {
    const val = getDisplayExpression(item);
    return val && String(val).toLowerCase() === String(inputValue.value).toLowerCase();
  });

  if (match) {
    handleItemSelect(match);
  } else if (props.useContains && inputValue.value) {
    isContains = true;
    handleItemSelect(inputValue.value);
  }
}

function handleEnter() {
  forceSelection();

  emit('enter-pressed');
}
onBeforeMount(async () => {
  if (props.apiUri) {
    if (selectedItem.value != null) {
      // prevent unnecessary reload if initial value is present
      setSelectedItemObjectFromValue();
      // Don't load all items eagerly when a value is selected
    } else if (!props.reloadOnFocusIn) {
      await loadItems(null);
    }
    await nextTick();
  } else {
    currentItems.value = props.items;
    if (selectedItem.value != null) {
      selectedItemObject.value = props.returnObject
        ? selectedItem.value
        : (currentItems.value.find(i => i[props.itemValue] === selectedItem.value) ?? null);
    }
  }
});
onMounted(async () => {
  if (props.autofocus === true && !props.readonly && !props.disabled) {
    if (props.apiUri && (props.reloadOnFocusIn !== true) && !selectedItem.value) {
      await loadItems(null);
    }
  }
  emit('mounted');
});
watch(() => props.items, newVal => {
  if (!props.apiUri) {
    currentItems.value = newVal;
  }
});

watch(modelValue, (value) => {
  if (value == null) {
    selectedItemObject.value = null;
    return;
  }

  setSelectedItemObjectFromValue(value);
});

defineExpose({
  selectedItem,
  selectedItemObject,
  forceSelection
});
</script>

<template>
  <div class="flex flex-col gap-1 p-1 w-full" :class="{ 'is-readonly': readonly }">
    <Label v-if="label" :for="staticId?.toString() || id" :disabled="disabled">
      {{ `${label}${required ? ' *' : ''}` }}
    </Label>

    <ComboboxRoot
      :model-value="selectedItemObject"
      :ignore-filter="!!apiUri"
      :open="isMenuOpen"
      :disabled="disabled"
      :open-on-focus="!disabled && !readonly"
      :open-on-click="!disabled && !readonly"
      :reset-search-term-on-blur="true"
      :reset-search-term-on-select="true"
      @update:model-value="handleItemSelect"
      @update:open="updateMenu"
    >
      <ComboboxAnchor :disabled="disabled" :readonly="readonly" :class="{ 'border-destructive ring-[0.5px] ring-destructive' : hasErrors }">
        <ComboboxInput
          :display-value="getDisplayExpression"
          :disabled="disabled"
          :auto-focus="autofocus"
          :id="staticId?.toString() || id"
          :placeholder="t('common.autocompletePlaceholder')"
          @input="(e) => inputChanged(e.target.value)"
          @focus="focusIn"
          @blur="onBlur"
          @keydown.enter="handleEnter"
        ></ComboboxInput>
        <template #append>
          <Button
            v-if="clearable && !readonly && !disabled && selectedItemObject"
            variant="ghost"
            size="icon-sm"
            class="shrink-0 size-6 text-muted-foreground hover:text-foreground"
            tabindex="-1"
            @click.stop="valueReset"
          >
            <X :size="14" />
          </Button>
        </template>
      </ComboboxAnchor>

      <ComboboxContent @scroll="detectScrollEnd">
            <template v-if="inputValue && useContains">
              <div
                @click="searchContains"
                class="flex items-center gap-1 rounded p-1.5 text-sm cursor-pointer hover:bg-accent hover:text-accent-foreground whitespace-nowrap"
              >
                <i>{{ containsPrefix }}</i>: {{ inputValue }}
              </div>
              <div class="my-1 h-px bg-border" />
            </template>

            <combobox-item
              v-for="(item, index) in currentItems"
              :key="item[itemValue] ?? index"
              :value="item"
              class="relative flex cursor-pointer select-none items-center rounded p-1.5 text-sm outline-none whitespace-nowrap data-highlighted:bg-accent data-highlighted:text-accent-foreground data-disabled:pointer-events-none data-disabled:opacity-50"
            >
              {{ getDisplayExpression(item) }}
            </combobox-item>

            <ComboboxEmpty>
              <div v-if="loading" class="flex items-center justify-center gap-2">
                <div class="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                <span class="italic">{{ t('common.loading') }}</span>
              </div>
              <span v-else>{{ t('common.noData') }}</span>
            </ComboboxEmpty>

            <div
              v-if="loading && currentItems.length > 0"
              class="flex items-center justify-center gap-2 py-1.5 text-sm text-muted-foreground pointer-events-none"
            >
              <div class="h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent" />
              <span class="italic">{{ t('common.loading') }}</span>
            </div>
      </ComboboxContent>
    </ComboboxRoot>

    <p v-if="hint" class="text-xs text-muted-foreground mb-1">{{ hint }}</p>
    <p v-if="errorMessage" class="text-xs text-destructive">{{ errorMessage }}</p>
  </div>
</template>

<style lang="scss" scoped>
.is-readonly {
  cursor: not-allowed;

  * {
    cursor: not-allowed;
    pointer-events: none;
  }
}
</style>
